import * as React from "react";
import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { GET_TECHNICIANS } from "../../graphql/queries/getTechnicians";
import { Query, Technician } from "../../generated/nest-graphql";
import { ImpersonatedTechnician } from "../../lib/qaImpersonation";

/**
 * QA-IMPERSONATION — market first, then a technician from that market.
 *
 * One query fetches every active technician and the markets are derived from it, so
 * the list can never offer a market with nobody in it and there is no second
 * round-trip when drilling in.
 */
export const ImpersonationDialog: React.FC<{
  open: boolean;
  current: ImpersonatedTechnician | null;
  onClose: () => void;
  onSelect: (technician: ImpersonatedTechnician) => void;
  onStop: () => void;
}> = ({ open, current, onClose, onSelect, onStop }) => {
  const [market, setMarket] = useState<string | null>(null);

  const { data, loading } = useQuery<Query>(GET_TECHNICIANS, {
    // auth0Id must exist: impersonation swaps the authenticated subject, and a
    // technician without one cannot be resolved by it.
    variables: { filter: { isActive: true, isVirtual: false, auth0Id: { $exists: true, $ne: null } } },
    skip: !open,
  });

  const technicians = useMemo(
    () => ((data?.getTechnicians ?? []).filter(Boolean) as Technician[]).filter((technician) => !!technician?.market),
    [data]
  );

  const markets = useMemo(() => {
    const counts = new Map<string, number>();
    for (const technician of technicians) {
      const key = technician.market as string;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [technicians]);

  const inMarket = useMemo(
    () =>
      technicians
        .filter((technician) => technician.market === market)
        .sort((a, b) => `${a.lastName ?? ""}`.localeCompare(`${b.lastName ?? ""}`)),
    [technicians, market]
  );

  const close = () => {
    setMarket(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle disableTypography>
        <span className="flex items-center gap-2">
          {market && (
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => setMarket(null)}
              style={{ textTransform: "none" }}
            >
              Markets
            </Button>
          )}
          <Typography variant="h6">{market ?? "Select a market"}</Typography>
        </span>
      </DialogTitle>

      {loading && <LinearProgress />}

      <DialogContent dividers style={{ padding: 0 }}>
        {!market ? (
          <List>
            {markets.map(([name, count]) => (
              <ListItem key={name} button divider onClick={() => setMarket(name)}>
                <ListItemText primary={name} secondary={`${count} ${count === 1 ? "technician" : "technicians"}`} />
                <ChevronRightIcon style={{ color: "#94a3b8" }} />
              </ListItem>
            ))}
            {!loading && markets.length === 0 && (
              <Typography variant="body2" className="text-gray-400 px-4 py-3">
                No active technicians found.
              </Typography>
            )}
          </List>
        ) : (
          <List>
            {inMarket.map((technician) => {
              const name = `${technician.firstName ?? ""} ${technician.lastName ?? ""}`.trim() || "Unnamed technician";
              return (
                <ListItem
                  key={technician.id}
                  button
                  divider
                  selected={current?.id === technician.id}
                  onClick={() => {
                    onSelect({ id: technician.id, name, market: technician.market as string });
                    setMarket(null);
                  }}
                >
                  <ListItemText
                    primary={name}
                    secondary={current?.id === technician.id ? "Currently viewing" : undefined}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        {current && (
          <Button onClick={onStop} style={{ textTransform: "none", color: "#fca5a5" }}>
            Stop impersonating
          </Button>
        )}
        <Button onClick={close} style={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
